import { useState, useRef } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useEditorStore } from '../../store/editor.store'
import { CanvasBlock } from './CanvasBlock'
import type { SectionState, SectionLayout } from '../../store/editor.store'
import { DESIGN_STYLE_MAP } from '../../data/designStyles'

function buildSectionLayoutCss(l?: SectionLayout): React.CSSProperties {
  if (!l || l.display === 'block' || !l.display) return {}
  if (l.display === 'flex') {
    return {
      display: 'flex',
      flexDirection: l.flexDirection ?? 'column',
      flexWrap: l.flexWrap ?? 'nowrap',
      gap: l.gap,
      alignItems: l.alignItems,
      justifyContent: l.justifyContent,
    }
  }
  if (l.display === 'grid') {
    return {
      display: 'grid',
      gridTemplateColumns: l.gridCols ?? 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: l.gap,
    }
  }
  return {}
}
import { BASE, ensureToken } from '../../utils/api'

interface Props {
  section: SectionState
  device: 'desktop' | 'tablet' | 'mobile'
}

export function CanvasSection({ section, device }: Props) {
  const { selectedSectionId, selectSection, removeSection, duplicateSection, moveSectionUp, moveSectionDown, updateSection, page, settings } =
    useEditorStore()
  const selected = selectedSectionId === section.id
  const idx = page.sections.findIndex((s) => s.id === section.id)

  const [regenOpen, setRegenOpen] = useState(false)
  const [regenPrompt, setRegenPrompt] = useState('')
  const [regenLoading, setRegenLoading] = useState(false)
  const [regenError, setRegenError] = useState<string | null>(null)
  const promptRef = useRef<HTMLTextAreaElement>(null)

  const openRegen = (e: React.MouseEvent) => {
    e.stopPropagation()
    setRegenPrompt('')
    setRegenError(null)
    setRegenOpen(true)
    setTimeout(() => promptRef.current?.focus(), 50)
  }

  const closeRegen = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setRegenOpen(false)
    setRegenError(null)
  }

  const runRegen = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setRegenLoading(true)
    setRegenError(null)
    try {
      const token = await ensureToken()
      const blockTypes = section.blocks.map((b) => b.type).join(', ')
      const pageContext = `Секция "${section.name ?? `Секция ${idx + 1}`}" (блоки: ${blockTypes || 'пусто'}). Стиль сайта: ${settings.designStyle || 'noir'}.`
      const res = await fetch(`${BASE}/ai/generate-section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: regenPrompt.trim() || `Переделай эту секцию — сделай лучше`, pageContext, style: settings.designStyle || undefined }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as { blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }
      if (!Array.isArray(data.blocks) || data.blocks.length === 0) throw new Error('Пустой ответ от AI')
      updateSection(section.id, { blocks: data.blocks as SectionState['blocks'] })
      setRegenOpen(false)
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : String(err))
    } finally {
      setRegenLoading(false)
    }
  }

  const { setNodeRef, isOver } = useDroppable({
    id: `section-${section.id}`,
    data: { type: 'section', sectionId: section.id },
  })

  const widthMap = { desktop: '100%', tablet: '768px', mobile: '390px' }

  const secStyleDef = section.sectionStyle ? DESIGN_STYLE_MAP[section.sectionStyle] : null
  const secStyleVars = secStyleDef ? secStyleDef.cssVars : {}

  const isEmpty = section.blocks.length === 0
  const hasVideo = !!section.backgroundVideo
  const hasBg = !hasVideo && section.background && section.background !== 'transparent'
  const overlayStyle = section.bgOverlay ? {
    position: 'absolute' as const, inset: 0, background: section.bgOverlay,
    opacity: (section.bgOverlayOpacity ?? 50) / 100, pointerEvents: 'none' as const, zIndex: 1,
  } : undefined

  // Empty sections are nearly invisible (2px separator) — expand only on drag or selection
  const emptyDropZoneHeight = isOver ? '120px' : selected ? '52px' : '2px'

  return (
    <div
      style={{
        position: 'relative',
        width: widthMap[device],
        margin: '0 auto',
        outline: selected ? '2px solid var(--accent-muted)' : '2px solid transparent',
        outlineOffset: '-2px',
        overflow: hasVideo ? 'hidden' : undefined,
        background: hasBg ? section.background : undefined,
        backgroundSize: section.backgroundSize ?? (hasBg && section.background?.startsWith('url(') ? 'cover' : undefined),
        backgroundPosition: section.backgroundPosition ?? (hasBg && section.background?.startsWith('url(') ? 'center' : undefined),
        backgroundRepeat: 'no-repeat',
        paddingTop: isEmpty ? 0 : section.paddingTop,
        paddingBottom: isEmpty ? 0 : section.paddingBottom,
        minHeight: isEmpty ? undefined : (section.fullHeight ? '100vh' : (section.minHeight || undefined)),
        transition: 'outline-color 0.15s',
      }}
      onClick={(e) => { e.stopPropagation(); selectSection(section.id) }}
    >
      {hasVideo && (
        <video
          autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, pointerEvents: 'none' }}
        >
          <source src={section.backgroundVideo} />
        </video>
      )}
      {overlayStyle && <div style={overlayStyle} />}
      {/* Section toolbar */}
      {selected && (
        <div className="section-toolbar">
          <span className="section-label">{section.name ?? `Секция ${idx + 1}`}</span>
          {section.anchorId && (
            <span title={`Якорь: #${section.anchorId}`} style={{ fontSize: '11px', color: 'var(--text-muted)', cursor: 'default' }}>#{section.anchorId}</span>
          )}
          {section.sectionAnimation && section.sectionAnimation !== 'none' && (
            <span title={`Анимация: ${section.sectionAnimation}`} style={{ fontSize: '11px', opacity: 0.6, cursor: 'default' }}>✨</span>
          )}
          <button title="Вверх" onClick={(e) => { e.stopPropagation(); moveSectionUp(section.id) }}>↑</button>
          <button title="Вниз" onClick={(e) => { e.stopPropagation(); moveSectionDown(section.id) }}>↓</button>
          <button title="Дублировать секцию (Ctrl+D)" onClick={(e) => { e.stopPropagation(); duplicateSection(section.id) }}>⧉</button>
          <button
            title="Переделать секцию с помощью AI"
            style={{ color: regenOpen ? 'var(--accent)' : undefined, fontWeight: 600 }}
            onClick={openRegen}
          >✦</button>
          <button
            className="section-delete-btn"
            title="Удалить секцию"
            onClick={(e) => { e.stopPropagation(); removeSection(section.id) }}
          >
            ✕
          </button>
        </div>
      )}
      {/* AI regeneration panel */}
      {selected && regenOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 32, left: 0, right: 0, zIndex: 100,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderTop: '2px solid var(--accent)', boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.04em' }}>
            ✦ AI — переделать секцию
          </div>
          <textarea
            ref={promptRef}
            value={regenPrompt}
            onChange={(e) => setRegenPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runRegen(e as unknown as React.MouseEvent) }}
            placeholder="Опиши что изменить (или оставь пустым — AI сам улучшит)"
            rows={2}
            style={{
              width: '100%', resize: 'vertical', fontSize: 12,
              background: 'var(--input-bg, #1a1d2e)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 4, padding: '6px 8px', outline: 'none',
            }}
          />
          {regenError && (
            <div style={{ fontSize: 11, color: '#f87171' }}>{regenError}</div>
          )}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button onClick={closeRegen} style={{ fontSize: 11, padding: '4px 10px' }}>Отмена</button>
            <button
              onClick={runRegen}
              disabled={regenLoading}
              style={{ fontSize: 11, padding: '4px 10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, cursor: regenLoading ? 'wait' : 'pointer', opacity: regenLoading ? 0.7 : 1 }}
            >
              {regenLoading ? 'Генерирую...' : 'Переделать ✦'}
            </button>
          </div>
        </div>
      )}

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        style={{
          position: (hasVideo || section.bgOverlay) ? 'relative' : undefined,
          zIndex: (hasVideo || section.bgOverlay) ? 2 : undefined,
          minHeight: isEmpty ? emptyDropZoneHeight : undefined,
          background: isOver ? 'rgba(91,106,240,0.06)' : undefined,
          transition: 'min-height 0.2s ease, background 0.15s',
          outline: isOver ? '2px dashed var(--accent)' : (isEmpty && !selected ? '1px dashed rgba(91,106,240,0.18)' : undefined),
          outlineOffset: '-4px',
        }}
      >
        <div
          className={secStyleDef ? 'bst-style-root' : undefined}
          style={{ ...(secStyleVars as React.CSSProperties), ...buildSectionLayoutCss(section.sectionLayout) }}
        >
          <SortableContext items={section.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            {section.blocks.map((block) => (
              <CanvasBlock key={block.id} block={block} sectionId={section.id} />
            ))}
          </SortableContext>

          {/* Drop hint: only shown when selected or dragging over */}
          {isEmpty && (selected || isOver) && (
            <div className="section-drop-hint">
              {isOver ? 'Отпустите для добавления' : 'Перетащите блок сюда'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
